import { useState } from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { SHARED_FILTER_OPTIONS } from '@/data/filters';

interface FilterState {
  categories: string[];
  useCases: string[];
  pricing: string[];
  platforms: string[];
  aiEnabled: boolean | null;
}

interface FilterSidebarProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

const filterOptions = SHARED_FILTER_OPTIONS;

interface FilterSectionProps {
  title: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  defaultOpen?: boolean;
}

function FilterSection({ title, options, selected, onChange, defaultOpen = true }: FilterSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleToggle = (option: string) => {
    const newSelected = selected.includes(option)
      ? selected.filter(item => item !== option)
      : [...selected, option];
    onChange(newSelected);
  };

  return (
    <div className="border-b border-border pb-4">
      <Button
        variant="ghost"
        className="w-full justify-between p-0 h-auto font-medium text-text-primary hover:text-accent"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{title}</span>
        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </Button>
      
      {isOpen && (
        <div className="mt-3 space-y-2">
          {options.map((option) => (
            <div key={option} className="flex items-center space-x-2">
              <Checkbox
                id={`${title}-${option}`}
                checked={selected.includes(option)}
                onCheckedChange={() => handleToggle(option)}
                className="data-[state=checked]:bg-accent data-[state=checked]:border-accent"
              />
              <label
                htmlFor={`${title}-${option}`}
                className="text-sm text-text-secondary cursor-pointer hover:text-text-primary transition-colors duration-fast"
              >
                {option}
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FilterSidebar({ filters, onFiltersChange, isOpen, onClose, className }: FilterSidebarProps) {
  const totalActiveFilters = 
    filters.categories.length + 
    filters.useCases.length + 
    filters.pricing.length + 
    filters.platforms.length + 
    (filters.aiEnabled !== null ? 1 : 0);

  const clearAllFilters = () => {
    onFiltersChange({
      categories: [],
      useCases: [],
      pricing: [],
      platforms: [],
      aiEnabled: null
    });
  };

  const handleAiToggle = (enabled: boolean) => {
    onFiltersChange({
      ...filters,
      aiEnabled: filters.aiEnabled === enabled ? null : enabled
    });
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      <aside className={cn(
        "fixed top-16 left-0 h-[calc(100vh-4rem)] w-80 bg-background border-r border-border transform transition-transform duration-base z-50 lg:sticky lg:transform-none lg:z-auto",
        isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        className
      )}>
        <div className="h-full overflow-y-auto scrollbar-thin p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <h2 className="text-lg font-heading font-semibold text-text-primary">Filters</h2>
              {totalActiveFilters > 0 && (
                <Badge variant="secondary" className="bg-accent-light text-accent">
                  {totalActiveFilters}
                </Badge>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              {totalActiveFilters > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-text-tertiary hover:text-error text-xs"
                >
                  Clear all
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="lg:hidden text-text-tertiary hover:text-text-primary"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Filter sections */}
          <div className="space-y-6">
            <FilterSection
              title="Category"
              options={filterOptions.categories}
              selected={filters.categories}
              onChange={(categories) => onFiltersChange({ ...filters, categories })}
            />
            
            <FilterSection
              title="Use Cases"
              options={filterOptions.useCases}
              selected={filters.useCases}
              onChange={(useCases) => onFiltersChange({ ...filters, useCases })}
            />
            
            <FilterSection
              title="Pricing"
              options={filterOptions.pricing}
              selected={filters.pricing}
              onChange={(pricing) => onFiltersChange({ ...filters, pricing })}
            />
            
            <FilterSection
              title="Platform"
              options={filterOptions.platforms}
              selected={filters.platforms}
              onChange={(platforms) => onFiltersChange({ ...filters, platforms })}
            />

            {/* AI Enabled */}
            <div className="border-b border-border pb-4">
              <h3 className="font-medium text-text-primary mb-3">AI Features</h3>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="ai-enabled"
                    checked={filters.aiEnabled === true}
                    onCheckedChange={() => handleAiToggle(true)}
                    className="data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                  />
                  <label
                    htmlFor="ai-enabled"
                    className="text-sm text-text-secondary cursor-pointer hover:text-text-primary transition-colors duration-fast"
                  >
                    AI-powered
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="ai-disabled"
                    checked={filters.aiEnabled === false}
                    onCheckedChange={() => handleAiToggle(false)}
                    className="data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                  />
                  <label
                    htmlFor="ai-disabled"
                    className="text-sm text-text-secondary cursor-pointer hover:text-text-primary transition-colors duration-fast"
                  >
                    Traditional tools
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}